// src/modules/VideoData.jsx
// Real browser-side video analysis using HTMLVideoElement + Canvas API
// Frame extraction → timeline → scene detect → color palette → motion → audio energy

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import {
  extractFrames, buildTimeline, detectScenes, extractColorPalette,
  getChannelEvolution, getHueDistribution, getVideoMetadata,
  computeMotionVectors, analyzeVideoAudio, rgbToHsv,
} from '../../utils/videoProcessing.js';
import { Panel, Metric, Tag, Spinner, InfoBox, Tabs, timestamp } from '../../components/ui.jsx';

const VIDEO_TABS = [
  { id: 'upload',     label: 'UPLOAD',    icon: '⬆' },
  { id: 'overview',   label: 'OVERVIEW',  icon: '◈', requiresData: true },
  { id: 'timeline',   label: 'TIMELINE',  icon: '⬟', requiresData: true },
  { id: 'scenes',     label: 'SCENES',    icon: '◎', requiresData: true },
  { id: 'colors',     label: 'COLORS',    icon: '◉', requiresData: true },
  { id: 'motion',     label: 'MOTION',    icon: '⬡', requiresData: true },
  { id: 'frames',     label: 'FRAMES',    icon: '◫', requiresData: true },
  { id: 'audio',      label: 'AUDIO',     icon: '◐', requiresData: true },
];

export default function VideoData({ mode }) {
  const [tab, setTab] = useState('upload');
  const [videoSrc, setVideoSrc] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [status, setStatus] = useState('');

  // Analysis results
  const [metadata, setMetadata] = useState(null);
  const [frames, setFrames] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [globalPalette, setGlobalPalette] = useState([]);
  const [channelEvolution, setChannelEvolution] = useState([]);
  const [audioAnalysis, setAudioAnalysis] = useState(null);
  const [sceneThreshold, setSceneThreshold] = useState(20);

  // Frame viewer
  const [activeFrame, setActiveFrame] = useState(0);
  const [frameHue, setFrameHue] = useState([]);
  const [motionVectors, setMotionVectors] = useState([]);

  // Playback
  const [playing, setPlaying] = useState(false);

  const fileRef = useRef();
  const videoRef = useRef();

  const handleFile = (file) => {
    if (!file || !file.type.startsWith('video/')) {
      alert('Please upload a video file (MP4, WebM, MOV, AVI...)'); return;
    }
    const url = URL.createObjectURL(file);
    setVideoFile(file);
    setVideoSrc(url);
    setTab('upload'); // Stay until video loads
  };

  // Called when video metadata is loaded
  const handleVideoLoaded = useCallback(async () => {
    if (!videoRef.current || !videoFile) return;
    const vid = videoRef.current;
    setProcessing(true);
    setProgress(0);

    try {
      setStatus('Reading metadata...');
      const meta = getVideoMetadata(vid, videoFile);
      setMetadata(meta);

      // Extract frames
      const numFrames = Math.min(24, Math.ceil(vid.duration * 2));
      setStatus(`Extracting ${numFrames} frames...`);
      const extractedFrames = await extractFrames(vid, numFrames, (pct) => {
        setProgress(pct);
        setStatus(`Extracting frames... ${pct}%`);
      });
      setFrames(extractedFrames);

      setStatus('Building timeline...');
      const tl = buildTimeline(extractedFrames);
      setTimeline(tl);

      setStatus('Detecting scenes...');
      const sc = detectScenes(extractedFrames, sceneThreshold);
      setScenes(sc);

      setStatus('Extracting color palette...');
      // Use middle frame for global palette
      const midFrame = extractedFrames[Math.floor(extractedFrames.length / 2)];
      if (midFrame) {
        const palette = extractColorPalette(midFrame.imageData, 8);
        setGlobalPalette(palette);

        const hue = getHueDistribution(midFrame.imageData, 36);
        setFrameHue(hue);
      }

      setStatus('Analyzing color evolution...');
      const ce = getChannelEvolution(extractedFrames);
      setChannelEvolution(ce);

      setStatus('Computing motion...');
      if (extractedFrames.length >= 2) {
        const mv = computeMotionVectors(extractedFrames[0], extractedFrames[1]);
        setMotionVectors(mv);
      }

      setStatus('Analyzing audio...');
      const audio = await analyzeVideoAudio(videoFile);
      setAudioAnalysis(audio);

      setStatus('Done!');
      setTab('overview');
    } catch (e) {
      console.error('Video analysis error:', e);
      setStatus('Error: ' + e.message);
    }
    setProcessing(false);
  }, [videoFile, sceneThreshold]);

  useEffect(() => {
    if (!videoRef.current || !videoSrc) return;
    const vid = videoRef.current;
    vid.onloadedmetadata = handleVideoLoaded;
    vid.src = videoSrc;
  }, [videoSrc, handleVideoLoaded]);

  // Update scene detection when threshold changes
  useEffect(() => {
    if (frames.length > 0) {
      setScenes(detectScenes(frames, sceneThreshold));
    }
  }, [sceneThreshold, frames]);

  // Update hue distribution when active frame changes
  useEffect(() => {
    if (frames[activeFrame]) {
      setFrameHue(getHueDistribution(frames[activeFrame].imageData, 36));
      if (activeFrame > 0) {
        setMotionVectors(computeMotionVectors(frames[activeFrame - 1], frames[activeFrame]));
      }
    }
  }, [activeFrame, frames]);

  return (
    <div className="anim-fadeup" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {/* Hidden video element for processing */}
      <video ref={videoRef} style={{ display: 'none' }} crossOrigin="anonymous" preload="auto" muted />

      <Tabs tabs={VIDEO_TABS} active={tab} onChange={setTab} dataset={frames.length > 0 ? true : null} />

      {/* ══ UPLOAD ══ */}
      {tab === 'upload' && (
        <div style={{ display: 'grid', gridTemplateColumns: videoSrc ? '1fr 1fr' : '1fr', gap: '14px' }}>
          <div
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={e => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => !videoSrc && fileRef.current?.click()}
            style={{ border: `2px dashed ${isDragging ? 'var(--orange)' : 'var(--b1)'}`, borderRadius: '4px', padding: '52px 20px', textAlign: 'center', cursor: videoSrc ? 'default' : 'pointer', background: isDragging ? 'rgba(255,112,64,.08)' : 'var(--surface)', transition: 'all .2s' }}>
            <input ref={fileRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
            {processing ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
                <Spinner size={28} />
                <div style={{ fontSize: '12px', color: 'var(--orange)' }}>{status}</div>
                <div style={{ width: '260px', height: '6px', background: 'var(--b0)', borderRadius: '3px' }}>
                  <div style={{ height: '100%', width: `${progress}%`, background: 'var(--orange)', borderRadius: '3px', transition: 'width .3s' }} />
                </div>
                <div style={{ fontSize: '11px', color: 'var(--t3)' }}>{progress}% — extracting frames & computing analysis</div>
              </div>
            ) : videoSrc ? (
              <div>
                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '14px', color: 'var(--green)', marginBottom: '8px' }}>✓ Video loaded — click a tab to explore</div>
                <button onClick={() => { setVideoSrc(null); setFrames([]); setTab('upload'); setMetadata(null); }}
                  style={{ padding: '6px 14px', background: 'transparent', border: '1px solid var(--b1)', color: 'var(--t3)', fontSize: '10px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono' }}>
                  Load Different Video
                </button>
              </div>
            ) : (
              <>
                <div style={{ fontSize: '40px', opacity: .4, marginBottom: '14px' }}>▶</div>
                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '18px', marginBottom: '7px' }}>Drop Video File Here</div>
                <div style={{ fontSize: '12px', color: 'var(--t3)', marginBottom: '16px' }}>MP4, WebM, MOV, AVI, MKV supported</div>
                <div style={{ fontSize: '11px', color: 'var(--t3)', background: 'var(--raised)', padding: '10px 14px', borderRadius: '3px', display: 'inline-block' }}>
                  Analysis is 100% in-browser — no upload to server
                </div>
              </>
            )}
          </div>

          {videoSrc && !processing && (
            <Panel title="VIDEO PREVIEW" accent="var(--orange)">
              <video src={videoSrc} controls style={{ width: '100%', borderRadius: '2px', border: '1px solid var(--b1)', maxHeight: '280px' }} />
            </Panel>
          )}
        </div>
      )}

      {/* ══ OVERVIEW ══ */}
      {tab === 'overview' && metadata && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
            <Metric label="Resolution" value={metadata.resolution} color="var(--orange)" />
            <Metric label="Duration" value={metadata.duration} unit="s" color="var(--cyan)" />
            <Metric label="File Size" value={metadata.fileSize} color="var(--gold)" />
            <Metric label="Megapixels" value={metadata.megapixels} unit="MP" color="var(--violet)" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
            <Metric label="Frames Analyzed" value={frames.length} color="var(--green)" sub="extracted for analysis" />
            <Metric label="Scenes Detected" value={scenes.length} color="var(--red)" sub={`threshold=${sceneThreshold}`} />
            <Metric label="Aspect Ratio" value={metadata.aspectRatio} color="var(--t2)" />
            <Metric label="Dominant Color" value={globalPalette[0] ? '#' + globalPalette[0].hex.slice(1).toUpperCase() : '—'} color={globalPalette[0]?.hex || 'var(--t2)'} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            {/* Frame strip */}
            <Panel title="FRAME STRIP" badge={`${frames.length} frames`} accent="var(--orange)" noPad>
              <div style={{ display: 'flex', gap: '2px', overflowX: 'auto', padding: '10px' }}>
                {frames.map((f, i) => (
                  <div key={i} onClick={() => { setActiveFrame(i); setTab('frames'); }}
                    style={{ flexShrink: 0, cursor: 'pointer', position: 'relative' }}>
                    <img src={f.dataUrl} alt={`frame ${i}`} style={{ width: '90px', height: '60px', objectFit: 'cover', borderRadius: '2px', border: `1px solid var(--b1)`, display: 'block', transition: 'border-color .15s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--orange)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--b1)'}
                    />
                    <div style={{ position: 'absolute', bottom: '2px', left: '2px', fontSize: '8px', background: 'rgba(0,0,0,.8)', padding: '1px 4px', borderRadius: '1px', color: 'var(--t2)' }}>
                      {f.time.toFixed(1)}s
                    </div>
                  </div>
                ))}
              </div>
            </Panel>

            {/* Color palette */}
            <Panel title="DOMINANT COLOR PALETTE" badge="k-means (k=8)" accent="var(--orange)">
              {globalPalette.length > 0 ? (
                <>
                  <div style={{ display: 'flex', height: '50px', borderRadius: '3px', overflow: 'hidden', marginBottom: '12px' }}>
                    {globalPalette.map((c, i) => (
                      <div key={i} style={{ flex: c.pct, background: c.hex, title: `${c.hex} (${c.pct}%)` }} title={`${c.hex} — ${c.pct}%`} />
                    ))}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                    {globalPalette.map((c, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: '20px', height: '20px', background: c.hex, borderRadius: '2px', flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--t1)' }}>{c.hex}</div>
                          <div style={{ fontSize: '9px', color: 'var(--t3)' }}>{c.pct}%</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : <InfoBox>No palette computed.</InfoBox>}
            </Panel>
          </div>
        </div>
      )}

      {/* ══ TIMELINE ══ */}
      {tab === 'timeline' && timeline.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Panel title="BRIGHTNESS & CONTRAST OVER TIME" accent="var(--orange)">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                <XAxis dataKey="time" stroke="var(--t3)" tick={{ fontSize: 9 }} label={{ value: 'time (s)', position: 'insideBottom', offset: -2, fill: 'var(--t3)', fontSize: 9 }} />
                <YAxis stroke="var(--t3)" tick={{ fontSize: 9 }} domain={[0, 255]} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="brightness" stroke="var(--gold)" strokeWidth={2} dot={false} name="Brightness" />
                <Line type="monotone" dataKey="contrast" stroke="var(--cyan)" strokeWidth={1.5} dot={false} name="Contrast" strokeDasharray="4 2" />
              </LineChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="MOTION INTENSITY OVER TIME" accent="var(--red)">
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                <XAxis dataKey="time" stroke="var(--t3)" tick={{ fontSize: 9 }} label={{ value: 'time (s)', position: 'insideBottom', offset: -2, fill: 'var(--t3)', fontSize: 9 }} />
                <YAxis stroke="var(--t3)" tick={{ fontSize: 9 }} />
                <Tooltip />
                <Area type="monotone" dataKey="motion" stroke="var(--red)" fill="var(--red)" fillOpacity={0.2} strokeWidth={2} name="Motion Δ" />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="RGB CHANNEL EVOLUTION" accent="var(--orange)">
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={channelEvolution}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                <XAxis dataKey="time" stroke="var(--t3)" tick={{ fontSize: 9 }} label={{ value: 'time (s)', position: 'insideBottom', offset: -2, fill: 'var(--t3)', fontSize: 9 }} />
                <YAxis stroke="var(--t3)" tick={{ fontSize: 9 }} domain={[0, 255]} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="R" stroke="var(--red)" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="G" stroke="var(--green)" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="B" stroke="var(--blue)" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Panel>
        </div>
      )}

      {/* ══ SCENES ══ */}
      {tab === 'scenes' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '10px', color: 'var(--t3)', marginBottom: '5px' }}>SCENE CUT THRESHOLD (frame diff): {sceneThreshold}</div>
              <input type="range" min={5} max={60} step={5} value={sceneThreshold}
                onChange={e => setSceneThreshold(parseInt(e.target.value))}
                style={{ width: '200px', accentColor: 'var(--orange)' }} />
            </div>
            <Metric label="Scenes Detected" value={scenes.length} color="var(--orange)" />
            <Metric label="Avg Scene Duration" value={scenes.length > 0 ? (scenes.reduce((s, sc) => s + sc.duration, 0) / scenes.length).toFixed(1) : '—'} unit="s" color="var(--t2)" />
          </div>

          {/* Scene timeline bar */}
          {scenes.length > 0 && metadata && (
            <Panel title="SCENE MAP" accent="var(--orange)">
              <div style={{ display: 'flex', height: '36px', borderRadius: '3px', overflow: 'hidden', gap: '1px', marginBottom: '10px' }}>
                {scenes.map((sc, i) => {
                  const pct = (sc.duration / metadata.duration) * 100;
                  return (
                    <div key={i}
                      style={{ flex: `0 0 ${pct}%`, background: `hsl(${i * 45}, 60%, 45%)`, opacity: 0.8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', minWidth: '2px' }}
                      title={`Scene ${i + 1}: ${sc.startTime.toFixed(1)}s — ${sc.endTime.toFixed(1)}s (${sc.duration}s)`}
                    >
                      {pct > 4 && <span style={{ fontSize: '9px', color: '#fff', fontWeight: 700 }}>{i + 1}</span>}
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: '10px', color: 'var(--t3)' }}>Each block = one scene. Width proportional to duration. Click any block for details.</div>
            </Panel>
          )}

          {/* Scene thumbnails */}
          <Panel title="SCENE THUMBNAILS" badge={`${scenes.length} scenes`} accent="var(--orange)" noPad>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px', padding: '12px' }}>
              {scenes.map((sc, i) => (
                <div key={i} style={{ background: 'var(--raised)', border: '1px solid var(--b0)', borderRadius: '3px', overflow: 'hidden' }}>
                  <img src={sc.thumbnail} alt={`scene ${i + 1}`} style={{ width: '100%', height: '100px', objectFit: 'cover', display: 'block' }} />
                  <div style={{ padding: '8px 10px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--t1)', fontWeight: 600, marginBottom: '3px' }}>Scene {i + 1}</div>
                    <div style={{ fontSize: '9px', color: 'var(--t3)' }}>
                      {sc.startTime.toFixed(2)}s → {sc.endTime.toFixed(2)}s · {sc.duration}s duration
                    </div>
                    <div style={{ fontSize: '9px', color: 'var(--orange)', marginTop: '2px' }}>Δ={sc.diff} (cut intensity)</div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ══ COLORS ══ */}
      {tab === 'colors' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            {/* Per-frame palette grid */}
            <Panel title="PER-FRAME COLOR PALETTES" accent="var(--orange)" noPad>
              <div style={{ overflowX: 'auto', padding: '10px' }}>
                {frames.slice(0, 12).map((frame, fi) => {
                  const palette = extractColorPalette(frame.imageData, 5);
                  return (
                    <div key={fi} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                      <div style={{ fontSize: '9px', color: 'var(--t3)', width: '35px', flexShrink: 0 }}>{frame.time.toFixed(1)}s</div>
                      <div style={{ display: 'flex', flex: 1, height: '22px', borderRadius: '2px', overflow: 'hidden' }}>
                        {palette.map((c, pi) => (
                          <div key={pi} style={{ flex: c.pct, background: c.hex, minWidth: '2px' }} title={`${c.hex} (${c.pct}%)`} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>

            {/* Hue distribution */}
            <Panel title="HUE DISTRIBUTION (current frame)" accent="var(--orange)">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={frameHue}>
                  <XAxis dataKey="hue" stroke="var(--t3)" tick={{ fontSize: 8 }} interval={5} label={{ value: 'hue°', position: 'insideBottom', offset: -2, fill: 'var(--t3)', fontSize: 9 }} />
                  <YAxis stroke="var(--t3)" tick={{ fontSize: 8 }} />
                  <Tooltip formatter={(v, n, p) => [v, `hue ${p.payload.hue}°`]} />
                  <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                    {frameHue.map((h, i) => <Cell key={i} fill={`hsl(${h.hue}, 80%, 55%)`} opacity={0.9} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px' }}>
                <span style={{ fontSize: '10px', color: 'var(--t3)' }}>Frame:</span>
                <input type="range" min={0} max={Math.max(0, frames.length - 1)} value={activeFrame}
                  onChange={e => setActiveFrame(parseInt(e.target.value))}
                  style={{ flex: 1, accentColor: 'var(--orange)' }} />
                <span style={{ fontSize: '10px', color: 'var(--orange)' }}>
                  {frames[activeFrame] ? `t=${frames[activeFrame].time.toFixed(1)}s` : ''}
                </span>
              </div>
              {frames[activeFrame] && (
                <img src={frames[activeFrame].dataUrl} alt="selected frame" style={{ width: '100%', marginTop: '10px', borderRadius: '2px', border: '1px solid var(--b1)' }} />
              )}
            </Panel>
          </div>

          {/* Global palette breakdown */}
          {globalPalette.length > 0 && (
            <Panel title="GLOBAL DOMINANT PALETTE" badge="k-means (k=8)" accent="var(--orange)">
              <div style={{ display: 'flex', height: '60px', borderRadius: '3px', overflow: 'hidden', marginBottom: '14px' }}>
                {globalPalette.map((c, i) => (
                  <div key={i} style={{ flex: c.pct, background: c.hex, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '3px' }}>
                    {c.pct > 5 && <span style={{ fontSize: '9px', color: rgbToHsv(c.r, c.g, c.b).v > 50 ? '#000' : '#fff' }}>{c.pct}%</span>}
                  </div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '6px' }}>
                {globalPalette.map((c, i) => {
                  const hsv = rgbToHsv(c.r, c.g, c.b);
                  return (
                    <div key={i} style={{ textAlign: 'center' }}>
                      <div style={{ width: '100%', paddingTop: '100%', background: c.hex, borderRadius: '3px', marginBottom: '4px' }} />
                      <div style={{ fontSize: '9px', fontWeight: 700, color: 'var(--t1)' }}>{c.hex}</div>
                      <div style={{ fontSize: '8px', color: 'var(--t3)' }}>H:{hsv.h}°</div>
                      <div style={{ fontSize: '8px', color: 'var(--t3)' }}>S:{hsv.s}%</div>
                      <div style={{ fontSize: '8px', color: 'var(--t3)' }}>{c.pct}%</div>
                    </div>
                  );
                })}
              </div>
            </Panel>
          )}
        </div>
      )}

      {/* ══ MOTION ══ */}
      {tab === 'motion' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
            <Metric label="Peak Motion" value={timeline.length > 0 ? Math.max(...timeline.map(t => t.motion)).toFixed(1) : '—'} color="var(--red)" sub="max frame diff" />
            <Metric label="Avg Motion" value={timeline.length > 0 ? (timeline.reduce((s, t) => s + t.motion, 0) / timeline.length).toFixed(1) : '—'} color="var(--orange)" sub="mean frame diff" />
            <Metric label="High-Motion Frames" value={timeline.filter(t => t.motion > 15).length} color="var(--red)" sub="diff > 15" />
          </div>

          <Panel title="MOTION HEATMAP (block-based optical flow)" accent="var(--red)">
            {frames[activeFrame] && motionVectors.length > 0 ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img src={frames[activeFrame].dataUrl} alt="motion frame" style={{ display: 'block', maxWidth: '100%', borderRadius: '2px' }} />
                <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                  {motionVectors.slice(0, 80).map((v, i) => (
                    <circle key={i} cx={`${(v.x / frames[activeFrame].w) * 100}%`} cy={`${(v.y / frames[activeFrame].h) * 100}%`}
                      r="3" fill={`rgba(255,${Math.max(0, 200 - v.magnitude * 3)},0,${Math.min(1, v.magnitude / 30)})`}
                    />
                  ))}
                </svg>
                <div style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '9px', background: 'rgba(0,0,0,.8)', padding: '3px 7px', borderRadius: '2px', color: 'var(--orange)' }}>
                  {motionVectors.length} motion blocks
                </div>
              </div>
            ) : <InfoBox color="var(--orange)" icon="◈">Select frame {'>'}0 to compute motion vectors between consecutive frames.</InfoBox>}
            <div style={{ marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '10px', color: 'var(--t3)' }}>Frame:</span>
              <input type="range" min={1} max={Math.max(1, frames.length - 1)} value={activeFrame || 1}
                onChange={e => setActiveFrame(parseInt(e.target.value))}
                style={{ flex: 1, accentColor: 'var(--orange)' }} />
              <span style={{ fontSize: '10px', color: 'var(--orange)' }}>
                {frames[activeFrame] ? `t=${frames[activeFrame].time.toFixed(1)}s` : ''}
              </span>
            </div>
          </Panel>

          <Panel title="FRAME-DIFF SCATTER" accent="var(--red)">
            <div style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '8px' }}>Motion intensity vs brightness — dark + high motion suggests action scenes.</div>
            <ResponsiveContainer width="100%" height={220}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                <XAxis dataKey="brightness" stroke="var(--t3)" tick={{ fontSize: 9 }} name="Brightness" label={{ value: 'brightness', position: 'insideBottom', offset: -2, fill: 'var(--t3)', fontSize: 9 }} />
                <YAxis dataKey="motion" stroke="var(--t3)" tick={{ fontSize: 9 }} name="Motion Δ" label={{ value: 'motion', angle: -90, position: 'insideLeft', fill: 'var(--t3)', fontSize: 9 }} />
                <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                <Scatter data={timeline} fill="var(--red)" opacity={0.65} />
              </ScatterChart>
            </ResponsiveContainer>
          </Panel>
        </div>
      )}

      {/* ══ FRAMES ══ */}
      {tab === 'frames' && frames.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '14px' }}>
          <Panel title="FRAME BROWSER" badge={`${frames.length} frames`} accent="var(--orange)" noPad>
            <div style={{ padding: '10px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '10px', color: 'var(--t3)' }}>Frame {activeFrame + 1}/{frames.length} — t={frames[activeFrame]?.time.toFixed(2)}s</span>
                <input type="range" min={0} max={frames.length - 1} value={activeFrame}
                  onChange={e => setActiveFrame(parseInt(e.target.value))}
                  style={{ flex: 1, accentColor: 'var(--orange)' }} />
              </div>
              {frames[activeFrame] && (
                <img src={frames[activeFrame].dataUrl} alt="frame" style={{ width: '100%', borderRadius: '3px', border: '1px solid var(--b1)' }} />
              )}
              {/* Frame strip */}
              <div style={{ display: 'flex', gap: '3px', overflowX: 'auto', marginTop: '8px' }}>
                {frames.map((f, i) => (
                  <img key={i} src={f.dataUrl} alt="" onClick={() => setActiveFrame(i)}
                    style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: '2px', border: `2px solid ${i === activeFrame ? 'var(--orange)' : 'transparent'}`, cursor: 'pointer', flexShrink: 0 }} />
                ))}
              </div>
            </div>
          </Panel>

          {/* Frame stats */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {frames[activeFrame] && (() => {
              const tl = timeline[activeFrame];
              const palette = extractColorPalette(frames[activeFrame].imageData, 5);
              return (
                <>
                  <Panel title="FRAME STATS" accent="var(--orange)">
                    {tl && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px' }}>
                        <Metric label="Brightness" value={tl.brightness} color="var(--gold)" />
                        <Metric label="Contrast" value={tl.contrast} color="var(--cyan)" />
                        <Metric label="Motion Δ" value={tl.motion} color="var(--red)" />
                        <Metric label="Luma" value={tl.luma} color="var(--t2)" />
                        <Metric label="R Mean" value={tl.r} color="var(--red)" />
                        <Metric label="G Mean" value={tl.g} color="var(--green)" />
                        <Metric label="B Mean" value={tl.b} color="var(--blue)" />
                        <Metric label="Time" value={tl.time} unit="s" color="var(--orange)" />
                      </div>
                    )}
                  </Panel>
                  <Panel title="FRAME PALETTE">
                    <div style={{ display: 'flex', height: '28px', borderRadius: '2px', overflow: 'hidden', marginBottom: '8px' }}>
                      {palette.map((c, i) => <div key={i} style={{ flex: c.pct, background: c.hex }} title={c.hex} />)}
                    </div>
                    {palette.map((c, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <div style={{ width: '16px', height: '16px', background: c.hex, borderRadius: '2px' }} />
                        <span style={{ fontSize: '10px', color: 'var(--t1)' }}>{c.hex}</span>
                        <span style={{ fontSize: '9px', color: 'var(--t3)', marginLeft: 'auto' }}>{c.pct}%</span>
                      </div>
                    ))}
                  </Panel>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* ══ AUDIO ══ */}
      {tab === 'audio' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {!audioAnalysis ? (
            <InfoBox color="var(--orange)" icon="◈">Audio analysis not yet complete. Go to Overview to trigger it.</InfoBox>
          ) : !audioAnalysis.hasAudio ? (
            <InfoBox color="var(--gold)" icon="⚠">Could not decode audio track. The video may be muted or the format unsupported for audio decoding. Error: {audioAnalysis.error}</InfoBox>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                <Metric label="Duration" value={audioAnalysis.duration} unit="s" color="var(--violet)" />
                <Metric label="Sample Rate" value={(audioAnalysis.sampleRate / 1000).toFixed(1)} unit="kHz" color="var(--cyan)" />
                <Metric label="Peak Amplitude" value={audioAnalysis.peak} color="var(--red)" sub={`${(20 * Math.log10(Math.max(audioAnalysis.peak, 1e-10))).toFixed(1)} dBFS`} />
                <Metric label="RMS Level" value={audioAnalysis.rms} color="var(--green)" sub={`${(20 * Math.log10(Math.max(audioAnalysis.rms, 1e-10))).toFixed(1)} dBFS`} />
              </div>

              <Panel title="AUDIO ENERGY OVER TIME" badge="RMS amplitude" accent="var(--violet)">
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={audioAnalysis.energyOverTime}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                    <XAxis dataKey="time" stroke="var(--t3)" tick={{ fontSize: 9 }} label={{ value: 'time (s)', position: 'insideBottom', offset: -2, fill: 'var(--t3)', fontSize: 9 }} />
                    <YAxis stroke="var(--t3)" tick={{ fontSize: 9 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="rms" stroke="var(--violet)" fill="var(--violet)" fillOpacity={0.2} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </Panel>

              {/* Audio + Motion correlation */}
              {timeline.length > 0 && (
                <Panel title="AUDIO ENERGY vs VISUAL MOTION" accent="var(--violet)">
                  <div style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '8px' }}>
                    Do loud moments correlate with high visual motion? (e.g., action sequences)
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                      <XAxis dataKey="time" type="number" domain={[0, Math.max(metadata?.duration || 10, 1)]} stroke="var(--t3)" tick={{ fontSize: 9 }} label={{ value: 'time (s)', position: 'insideBottom', offset: -2, fill: 'var(--t3)', fontSize: 9 }} />
                      <YAxis stroke="var(--t3)" tick={{ fontSize: 9 }} />
                      <Tooltip />
                      <Legend />
                      <Line data={audioAnalysis.energyOverTime.map(e => ({ time: e.time, val: e.rms * 200 }))} type="monotone" dataKey="val" stroke="var(--violet)" strokeWidth={1.5} dot={false} name="Audio RMS (×200)" />
                      <Line data={timeline.map(t => ({ time: t.time, val: t.motion }))} type="monotone" dataKey="val" stroke="var(--red)" strokeWidth={1.5} dot={false} name="Visual Motion" />
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}